import {
  Activity,
  ActivityData,
  ActivityDirection,
  ActivityGroup,
  EntityActivityRequest,
  EntityActivityResponse,
  NewActivity,
  Notes,
  TranscriptFailureReason
} from "@/entities/activities/types";
import { getEnumTKey, mapEnum } from "@/utils/strings";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { TFunction } from "i18next";
import { format, isValid } from "date-fns";
import { groupBy, keys, mapValues, orderBy } from "lodash";
import { toCalendarDate } from "@/utils/datetime";

export const activityGroupToTypesMap = {
  [ActivityGroup.ALL.toString()]: [],
  [ActivityGroup.OTHERS.toString()]: [
    Activity.CALL,
    Activity.EMAIL,
    Activity.IN_PERSON_MEETING,
    Activity.ONLINE_MEETING
  ],
  [ActivityGroup.MEETINGS.toString()]: [
    Activity.IN_PERSON_MEETING,
    Activity.ONLINE_MEETING
  ],
  [ActivityGroup.CALLS.toString()]: [Activity.CALL],
  [ActivityGroup.EMAILS.toString()]: [Activity.EMAIL]
};

const DIRECTIONAL_ACTIVITY_TYPES = new Set<Activity>([
  Activity.CALL,
  Activity.EMAIL,
  Activity.TEXT
]);

// Whether an email, call or text was sent or received; undefined when the
// direction is unknown or the activity type has none.
export const getActivityDirection = (
  activity: ActivityData
): ActivityDirection | undefined => {
  if (
    activity.type === undefined ||
    !DIRECTIONAL_ACTIVITY_TYPES.has(activity.type)
  ) {
    return undefined;
  }
  const direction = activity.notes?.direction;
  return direction === "inbound" || direction === "outbound"
    ? direction
    : undefined;
};

export const newActivityTypes = mapEnum(NewActivity, (value) => value).filter(
  (value) => value !== NewActivity.SLACK_MESSAGE
);

// includeRecurrentInvites: false — the general activity tab renders all activities
// without series dedup; only UpcomingActivities sets this to true and routes through
// the timehook group-by-series path.
const DEFAULT_FETCH_FLAGS = {
  includeCancelledMeetings: true,
  includeRecurrentInvites: false
};

const mainActivityTypes = activityGroupToTypesMap[
  ActivityGroup.OTHERS.toString()
] as Activity[];

const othersActivityTypes = Object.values(Activity).filter(
  (value): value is Activity =>
    typeof value === "number" && !mainActivityTypes.includes(value)
);

export const buildFetchRequest = (
  activityGroup: string,
  direction?: ActivityDirection,
  fromDatetime?: string,
  toDatetime?: string
): EntityActivityRequest => {
  const request: EntityActivityRequest = {
    flags: { ...DEFAULT_FETCH_FLAGS }
  };

  if (activityGroup !== ActivityGroup.ALL.toString()) {
    const groupTypes =
      activityGroupToTypesMap[
        activityGroup as keyof typeof activityGroupToTypesMap
      ];

    if (activityGroup === ActivityGroup.OTHERS.toString()) {
      request.activityTypes = othersActivityTypes;
    } else if (groupTypes && groupTypes.length > 0) {
      request.activityTypes = groupTypes;
    }
  }

  if (direction) {
    request.direction = direction;
  }

  if (fromDatetime) {
    request.fromDatetime = fromDatetime;
  }

  if (toDatetime) {
    request.toDatetime = toDatetime;
  }

  return request;
};

export const ACCEPTED_TRANSCRIPT_EXTENSIONS = ".txt,.vtt,.srt";

export const isMeetingActivity = (activity: { type?: Activity }): boolean =>
  activity.type === Activity.IN_PERSON_MEETING ||
  activity.type === Activity.ONLINE_MEETING;

export const buildRemoveActivityTagUpdate = (
  activity: Pick<ActivityData, "tags">,
  tagId: string
): Pick<ActivityData, "tags"> => ({
  tags: (activity.tags ?? []).filter((tag) => tag !== tagId)
});

export const updateActivityTagsInFetchResponse = (
  fetchResponse: EntityActivityResponse | null,
  updatedActivity: ActivityData
): EntityActivityResponse | null => {
  if (!fetchResponse) return null;

  return {
    ...fetchResponse,
    activities: fetchResponse.activities.map((activity) =>
      activity.id === updatedActivity.id
        ? { ...activity, tags: updatedActivity.tags }
        : activity
    )
  };
};

// The transcript summary + autofill are still being generated. Driven by the
// authoritative backend status, with the legacy client-side heuristic (attachment
// present but not yet analyzed) kept as a fallback during rollout.
export const isTranscriptProcessing = (notes?: Notes | null): boolean =>
  notes?.transcriptProcessingStatus === "processing" ||
  (!notes?.hasTranscript && !!notes?.transcriptAttachmentId);

// The recording bot reached a terminal dead end — no transcript will ever arrive.
// Returns the reason so the UI can explain which dead end it was, and null when the
// meeting is fine or still in flight.
export const getTranscriptFailureReason = (
  notes?: Notes | null
): TranscriptFailureReason | null => {
  if (notes?.transcriptProcessingStatus !== "failed") return null;
  // An attachment id suppresses the notice as well as hasTranscript: neither writer
  // can produce failed-with-an-attachment. The connector refuses to mark a failure
  // once an attachment is present, and a manual upload forces the status back to
  // processing — so an attachment here means a transcript landed after the failure.
  if (notes?.hasTranscript || notes?.transcriptAttachmentId) return null;
  return notes?.transcriptFailureReason ?? "transcript_processing";
};

// Stable identifier for the record's current processing "window": the sorted
// ids of the meetings still being analyzed, or null when none are. Lets the
// banner dismissal survive a refresh yet re-appear once a different set of
// meetings starts processing.
export const getProcessingKey = (
  processingActivityIds: string[]
): string | null =>
  processingActivityIds.length > 0
    ? [...processingActivityIds].sort().join(",")
    : null;

export const isValidTranscriptFile = (file: File): boolean => {
  const extension = file.name.toLowerCase().split(".").pop();
  return ["txt", "vtt", "srt"].includes(extension || "");
};

export const generateTranscriptFilename = (
  meetingTitle?: string,
  extension = "txt"
): string => {
  const title = meetingTitle || "meeting";
  const slugified = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .substring(0, 50);
  const normalizedExtension = extension.toLowerCase().replace(/^\./, "");
  const safeExtension = ["txt", "vtt", "srt"].includes(normalizedExtension)
    ? normalizedExtension
    : "txt";
  return `${slugified || "meeting"}-transcript.${safeExtension}`;
};

export const getMustSelectCompanyText = (
  t: TFunction,
  entityType: EntityType
): string => {
  const entityName = t(
    getEnumTKey("entities", "names", EntityType, entityType)
  ).toLowerCase();
  return t("activities.activityModal.mustSelectCompany", { entityName });
};

const monthKey = (activity: ActivityData) => {
  if (!activity.notes?.date) return undefined;
  const day = toCalendarDate(activity.notes.date);
  // format throws on an unparseable date; fall back to the raw month prefix
  return isValid(day)
    ? format(day, "yyyy-MM")
    : activity.notes.date.slice(0, 7);
};

// Order by the shown calendar day; unparseable dates sort last
const dayTime = (activity: ActivityData) =>
  activity.notes?.date ? toCalendarDate(activity.notes.date).getTime() || 0 : 0;

// Months newest first, each holding its activities newest first, by the day each one shows
export const groupActivitiesByMonth = (activities: ActivityData[]) => {
  const grouped = groupBy(activities, monthKey);
  return {
    months: orderBy(keys(grouped), [(month) => month], ["desc"]),
    byMonth: mapValues(grouped, (monthActivities) =>
      orderBy(monthActivities, [dayTime], ["desc"])
    )
  };
};
